import React from 'react';
import Icon from './Icon';

/**
 * EGGS Button Component
 * Based on EGGS Design System specifications
 * 
 * @param {string} variant - brand | primary | secondary | outline | ghost | inverse
 * @param {string} size - lg | md | sm
 * @param {boolean} hasLeadingIcon - Show icon before text
 * @param {boolean} hasTrailingIcon - Show icon after text
 * @param {string} leadingIcon - Icon name for leading icon
 * @param {string} trailingIcon - Icon name for trailing icon
 * @param {React.ReactNode} children - Button content
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional button props
 */
export default function Button({
  variant = 'brand',
  size = 'lg',
  hasLeadingIcon = false,
  hasTrailingIcon = false,
  leadingIcon = 'arrow-left',
  trailingIcon = 'arrow-right',
  children,
  className = '',
  ...props
}) {
  // Brand buttons always show a leading arrow
  const showLeadingIcon = variant === 'brand' ? true : hasLeadingIcon;
  const resolvedLeadingIcon = variant === 'brand' && !hasLeadingIcon ? 'arrow-right' : leadingIcon;
  // Base classes for all buttons
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-burns',
    'font-medium',
    'min-h-[44px]',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'rounded-full',
    'cursor-pointer',
    'whitespace-nowrap',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-40',
    'disabled:cursor-not-allowed',
    'disabled:pointer-events-none',
    'active:scale-[0.98]'
  ].join(' ');

  // Variant-specific styles
  const variantClasses = {
    brand: [
      'bg-eggs-yellow',
      'text-eggs-dark',
      'border-0',
      'hover:bg-eggs-dark',
      'hover:text-eggs-yellow',
      'focus:ring-eggs-yellow'
    ].join(' '),
    
    primary: [
      'bg-eggs-dark',
      'text-white',
      'border-0',
      'hover:bg-eggs-dark-hover',
      'focus:ring-eggs-dark'
    ].join(' '),
    
    secondary: [
      'bg-eggs-secondary',
      'text-eggs-dark',
      'border-0',
      'hover:bg-eggs-secondary-hover',
      'focus:ring-eggs-secondary'
    ].join(' '),
    
    outline: [
      'bg-transparent',
      'text-eggs-dark',
      'border-2',
      'border-eggs-dark',
      'hover:bg-gray-50',
      'focus:ring-eggs-dark'
    ].join(' '),
    
    ghost: [
      'bg-transparent',
      'text-eggs-dark',
      'border-0',
      'hover:bg-gray-50',
      'focus:ring-eggs-dark'
    ].join(' '),
    
    inverse: [
      'bg-white',
      'text-eggs-dark',
      'border-0',
      'hover:bg-gray-50',
      'focus:ring-white'
    ].join(' ')
  };

  // Size-specific styles
  const sizeClasses = {
    lg: [
      'h-12',        // 48px
      'px-6',        // 24px
      'text-lg',     // 18px
      'gap-3'        // 12px
    ].join(' '),
    
    md: [
      'h-10',        // 40px
      'px-4',        // 16px
      'text-base',   // 16px
      'gap-2'        // 8px
    ].join(' '),
    
    sm: [
      'h-8',         // 32px
      'px-4',        // 16px
      'text-sm',     // 14px
      'gap-2'        // 8px
    ].join(' ')
  };

  // Icon sizes based on button size
  const iconSizes = {
    lg: 20,
    md: 18,
    sm: 16
  };

  const iconSize = iconSizes[size];

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {showLeadingIcon && <Icon name={resolvedLeadingIcon} size={iconSize} />}
      {children}
      {hasTrailingIcon && <Icon name={trailingIcon} size={iconSize} />}
    </button>
  );
}

// Export individual button variants for convenience
export const BrandButton = (props) => <Button variant="brand" {...props} />;
export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => <Button variant="secondary" {...props} />;
export const OutlineButton = (props) => <Button variant="outline" {...props} />;
export const GhostButton = (props) => <Button variant="ghost" {...props} />;
export const InverseButton = (props) => <Button variant="inverse" {...props} />;